import React, { useEffect, useRef } from 'react';
import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader';
import { FileLoader } from 'three';
import './ModelViewer.css';

const ModelViewer = ({ modelPath }) => {
    const containerRef = useRef(null);
    const sceneRef = useRef(null);
    const rendererRef = useRef(null);
    const cameraRef = useRef(null);
    const controlsRef = useRef(null);
    const modelRef = useRef(null);
    const animationFrameRef = useRef(null);

    useEffect(() => {
        if (!containerRef.current) return;

        // Scene setup
        const scene = new THREE.Scene();
        scene.background = null; // Transparent background
        sceneRef.current = scene;

        // Camera setup (same as Earth Day - 100mm equivalent)
        const focalLength = 100;
        const filmHeight = 24;
        const fov = 2 * Math.atan(filmHeight / (2 * focalLength)) * (180 / Math.PI);
        
        const camera = new THREE.PerspectiveCamera(
            fov,
            containerRef.current.clientWidth / containerRef.current.clientHeight,
            0.1,
            1000
        );
        camera.position.set(0, 0, 5);
        cameraRef.current = camera;

        // Renderer setup
        const renderer = new THREE.WebGLRenderer({
            antialias: true,
            powerPreference: "high-performance",
            precision: "highp",
            alpha: true // Enable transparency
        });
        renderer.toneMapping = THREE.ACESFilmicToneMapping;
        renderer.toneMappingExposure = 1;
        renderer.outputColorSpace = THREE.SRGBColorSpace;
        // Use a subtle background color so we can see if the canvas is rendering
        renderer.setClearColor(0x1a1a1a, 1); // Dark gray background for visibility
        renderer.setSize(containerRef.current.clientWidth, containerRef.current.clientHeight);
        renderer.setPixelRatio(window.devicePixelRatio);
        renderer.shadowMap.enabled = true;
        renderer.shadowMap.type = THREE.PCFSoftShadowMap;
        containerRef.current.appendChild(renderer.domElement);
        rendererRef.current = renderer;

        // Lighting setup - bright lighting for better visibility of 3D scan textures
        const ambientLight = new THREE.AmbientLight(0xffffff, 2.0); // Increased ambient for better texture visibility
        scene.add(ambientLight);

        const directionalLight1 = new THREE.DirectionalLight(0xffffff, 1.5);
        directionalLight1.position.set(2, 2, 2);
        directionalLight1.castShadow = true;
        directionalLight1.shadow.mapSize.width = 2048;
        directionalLight1.shadow.mapSize.height = 2048;
        directionalLight1.shadow.camera.near = 0.5;
        directionalLight1.shadow.camera.far = 500;
        directionalLight1.shadow.camera.left = -100;
        directionalLight1.shadow.camera.right = 100;
        directionalLight1.shadow.camera.top = 100;
        directionalLight1.shadow.camera.bottom = -100;
        scene.add(directionalLight1);

        const directionalLight2 = new THREE.DirectionalLight(0xffffff, 1.2);
        directionalLight2.position.set(-2, 2, -2);
        directionalLight2.castShadow = true;
        directionalLight2.shadow.mapSize.width = 2048;
        directionalLight2.shadow.mapSize.height = 2048;
        directionalLight2.shadow.camera.near = 0.5;
        directionalLight2.shadow.camera.far = 500;
        directionalLight2.shadow.camera.left = -100;
        directionalLight2.shadow.camera.right = 100;
        directionalLight2.shadow.camera.top = 100;
        directionalLight2.shadow.camera.bottom = -100;
        scene.add(directionalLight2);

        const directionalLight3 = new THREE.DirectionalLight(0xffffff, 1.2);
        directionalLight3.position.set(0, 3, 0);
        directionalLight3.castShadow = true;
        directionalLight3.shadow.mapSize.width = 2048;
        directionalLight3.shadow.mapSize.height = 2048;
        directionalLight3.shadow.camera.near = 0.5;
        directionalLight3.shadow.camera.far = 500;
        directionalLight3.shadow.camera.left = -100;
        directionalLight3.shadow.camera.right = 100;
        directionalLight3.shadow.camera.top = 100;
        directionalLight3.shadow.camera.bottom = -100;
        scene.add(directionalLight3);

        // OrbitControls (no auto-rotate)
        const controls = new OrbitControls(camera, renderer.domElement);
        controls.enableDamping = true;
        controls.dampingFactor = 0.05;
        controls.autoRotate = false; // No auto-rotation
        controls.enableZoom = true;
        controls.enablePan = true;
        controlsRef.current = controls;

        // Load model
        const loader = new GLTFLoader();
        
        // For GLB files, set the path for texture resolution
        const basePath = modelPath.substring(0, modelPath.lastIndexOf('/') + 1);
        
        // Set up the GLTFLoader to use the correct path for textures
        loader.setPath(basePath);
        
        // Ensure path starts with / and is relative to public folder
        // In React, files in public folder are served from root
        const fullPath = modelPath.startsWith('/') ? modelPath : '/' + modelPath;
        
        // For GLB files, load as ArrayBuffer using fetch, then parse manually
        // This prevents the loader from trying to parse binary GLB as JSON
        if (fullPath.endsWith('.glb')) {
            // Load the GLB file as ArrayBuffer using fetch
            fetch(fullPath)
                .then(response => {
                    if (!response.ok) {
                        throw new Error(`HTTP error! status: ${response.status}`);
                    }
                    return response.arrayBuffer();
                })
                .then(arrayBuffer => {
                    // Use GLTFLoader's parse method with proper callback
                    // Make sure the path includes the textures folder
                    const texturePath = basePath + 'textures/';
                    
                    return new Promise((resolve, reject) => {
                        // The parse method signature: parse(data, path, onLoad, onError)
                        loader.parse(
                            arrayBuffer,
                            basePath, // This should resolve texture paths relative to the GLB file
                            (gltf) => {
                                // SIMPLER APPROACH: Just configure what's already in the GLB
                                // Don't try to override or reload textures - use embedded ones as-is
                                // Only fix material properties to ensure textures display
                                
                                let materialCount = 0;
                                const materialInfo = [];
                                
                                gltf.scene.traverse((child) => {
                                    if (child.isMesh && child.material) {
                                        const materials = Array.isArray(child.material) ? child.material : [child.material];
                                        materials.forEach((mat) => {
                                            if (mat) {
                                                const matInfo = {
                                                    index: materialCount++,
                                                    type: mat.type,
                                                    hasMap: !!mat.map,
                                                    mapSize: mat.map && mat.map.image ? `${mat.map.image.width}x${mat.map.image.height}` : 'none',
                                                    color: mat.color ? mat.color.getHexString() : 'none',
                                                    roughness: mat.roughness !== undefined ? mat.roughness : 'N/A',
                                                    metalness: mat.metalness !== undefined ? mat.metalness : 'N/A'
                                                };
                                                materialInfo.push(matInfo);
                                                
                                                // Only configure material properties - don't reload textures
                                                if (mat.map && mat.map.image) {
                                                    // Texture exists - just ensure it's configured correctly
                                                    mat.map.colorSpace = THREE.SRGBColorSpace;
                                                    mat.map.flipY = false;
                                                    mat.map.needsUpdate = true;
                                                    
                                                    // Check if texture image is actually valid
                                                    const img = mat.map.image;
                                                    
                                                    // For large textures, optimize filtering
                                                    if (img.width > 2048) {
                                                        mat.map.minFilter = THREE.LinearMipmapLinearFilter;
                                                        mat.map.magFilter = THREE.LinearFilter;
                                                        mat.map.generateMipmaps = true;
                                                    }
                                                }
                                                
                                                // Configure material for texture visibility
                                                if (mat.isMeshStandardMaterial || mat.isMeshPhysicalMaterial) {
                                                    mat.color.setHex(0xffffff);
                                                    mat.roughness = 1.0; // 100% roughness for matte appearance
                                                    mat.metalness = 0.0; // No metalness
                                                    if (mat.emissive) mat.emissive.setHex(0x000000);
                                                    if (mat.emissiveIntensity !== undefined) mat.emissiveIntensity = 0;
                                                    mat.needsUpdate = true;
                                                }
                                            }
                                        });
                                    }
                                });
                                
                                resolve(gltf);
                            },
                            (error) => {
                                console.error('Parse error:', error);
                                reject(error);
                            }
                        );
                    });
                })
                .then((gltf) => {
                    const model = gltf.scene;
                    modelRef.current = model;

                    // Ensure materials are visible and properly set up
                    let meshCount = 0;
                    model.traverse((child) => {
                        if (child.isMesh) {
                            meshCount++;
                            child.castShadow = true;
                            child.receiveShadow = true;
                            
                            // Ensure material is visible and bright
                            if (child.material) {
                                const materials = Array.isArray(child.material) ? child.material : [child.material];
                                materials.forEach((mat, matIdx) => {
                                    if (mat) {
                                        // For 3D scans, convert non-standard materials to MeshStandardMaterial
                                        // This ensures textures display correctly
                                        if (!mat.isMeshStandardMaterial && !mat.isMeshPhysicalMaterial && mat.map) {
                                            const newMat = new THREE.MeshStandardMaterial({
                                                map: mat.map,
                                                color: mat.color ? mat.color.clone() : new THREE.Color(0xffffff),
                                                side: THREE.DoubleSide,
                                                roughness: 1.0, // 100% roughness for matte appearance
                                                metalness: 0.0
                                            });
                                            
                                            // Copy other texture maps if they exist
                                            if (mat.normalMap) newMat.normalMap = mat.normalMap;
                                            if (mat.roughnessMap) newMat.roughnessMap = mat.roughnessMap;
                                            if (mat.metalnessMap) newMat.metalnessMap = mat.metalnessMap;
                                            if (mat.aoMap) newMat.aoMap = mat.aoMap;
                                            
                                            // Replace the material
                                            if (Array.isArray(child.material)) {
                                                child.material[matIdx] = newMat;
                                            } else {
                                                child.material = newMat;
                                            }
                                            mat = newMat;
                                        }
                                        
                                        mat.needsUpdate = true;
                                        mat.side = THREE.DoubleSide;
                                        
                                        // Ensure material is not transparent if it shouldn't be
                                        if (mat.transparent && mat.opacity === 0) {
                                            mat.opacity = 1;
                                        }
                                        
                                        // For 3D scan materials with embedded textures, ensure they display correctly
                                        // Set material properties to ensure textures are visible
                                        if (mat.isMeshStandardMaterial || mat.isMeshPhysicalMaterial || mat.isMeshPhongMaterial || mat.isMeshLambertMaterial) {
                                            // Ensure material uses the texture properly
                                            if (mat.emissive) {
                                                mat.emissive.setHex(0x000000);
                                            }
                                            if (mat.emissiveIntensity !== undefined) {
                                                mat.emissiveIntensity = 0;
                                            }
                                            
                                            // Set base color to white so texture shows properly (not tinted)
                                            if (mat.color) {
                                                mat.color.setHex(0xffffff);
                                            }
                                            
                                            // For embedded textures from 3D scans, they might need special handling
                                            if (mat.map) {
                                                // Force texture update and color space
                                                mat.map.needsUpdate = true;
                                                mat.map.colorSpace = THREE.SRGBColorSpace;
                                                mat.map.flipY = false; // GLB textures are usually not flipped
                                                
                                                // Optimize texture settings for large textures
                                                // Large textures (4096x4096) can cause performance issues
                                                // Set appropriate filtering
                                                if (mat.map.image && mat.map.image.width > 2048) {
                                                    mat.map.minFilter = THREE.LinearMipmapLinearFilter;
                                                    mat.map.magFilter = THREE.LinearFilter;
                                                    mat.map.generateMipmaps = true;
                                                }
                                                
                                                // Ensure texture is properly set up
                                                if (mat.map.image) {
                                                    // For embedded textures, ensure they're properly configured
                                                    if (!mat.map.image.src) {
                                                        // Embedded texture - ensure it's ready and properly configured
                                                        if (mat.map.image.complete) {
                                                            mat.map.needsUpdate = true;
                                                            mat.needsUpdate = true;
                                                            // Force a render update after a short delay to ensure texture is applied
                                                            setTimeout(() => {
                                                                mat.map.needsUpdate = true;
                                                                mat.needsUpdate = true;
                                                                if (rendererRef.current && sceneRef.current && cameraRef.current) {
                                                                    rendererRef.current.render(sceneRef.current, cameraRef.current);
                                                                }
                                                            }, 100);
                                                        } else {
                                                            // Wait for image to load
                                                            mat.map.image.onload = () => {
                                                                mat.map.needsUpdate = true;
                                                                mat.needsUpdate = true;
                                                            };
                                                        }
                                                    }
                                                    
                                                    // CRITICAL: Validate texture and ensure it's being used
                                                    if (mat.isMeshStandardMaterial && mat.map && mat.map.image) {
                                                        const img = mat.map.image;
                                                        
                                                        // Validate texture image
                                                        if (!img.complete) {
                                                            img.onload = () => {
                                                                mat.map.needsUpdate = true;
                                                                mat.needsUpdate = true;
                                                            };
                                                        }
                                                        
                                                        // Ensure material properties allow texture to show
                                                        mat.color.setHex(0xffffff);
                                                        mat.roughness = 1.0; // 100% roughness for matte appearance
                                                        mat.metalness = 0.0;
                                                        if (mat.emissive) mat.emissive.setHex(0x000000);
                                                        if (mat.emissiveIntensity !== undefined) mat.emissiveIntensity = 0;
                                                        
                                                        // Ensure texture is properly configured
                                                        mat.map.colorSpace = THREE.SRGBColorSpace;
                                                        mat.map.flipY = false;
                                                        mat.map.needsUpdate = true;
                                                        mat.needsUpdate = true;
                                                    }
                                                }
                                            }
                                            
                                            // Update other texture maps
                                            if (mat.normalMap) {
                                                mat.normalMap.needsUpdate = true;
                                            }
                                            if (mat.roughnessMap) {
                                                mat.roughnessMap.needsUpdate = true;
                                            }
                                            if (mat.metalnessMap) {
                                                mat.metalnessMap.needsUpdate = true;
                                            }
                                        } else {
                                            // For other material types, still ensure textures are set up
                                            if (mat.map) {
                                                mat.map.needsUpdate = true;
                                                mat.map.colorSpace = THREE.SRGBColorSpace;
                                                mat.map.flipY = false;
                                                // Set base color to white
                                                if (mat.color) {
                                                    mat.color.setHex(0xffffff);
                                                }
                                            }
                                        }
                                    }
                                });
                            } else {
                                // If no material, add a default one
                                child.material = new THREE.MeshStandardMaterial({ 
                                    color: 0xffffff,
                                    side: THREE.DoubleSide
                                });
                            }
                            
                            // Make sure geometry is visible
                            child.visible = true;
                        }
                    });

                    // Get initial bounding box
                    const box = new THREE.Box3().setFromObject(model);
                    const size = box.getSize(new THREE.Vector3());
                    const center = box.getCenter(new THREE.Vector3());

                    // Check if bounding box is valid
                    if (size.x === 0 && size.y === 0 && size.z === 0) {
                        model.scale.set(1, 1, 1);
                        model.position.set(0, 0, 0);
                    } else {
                        const maxDim = Math.max(size.x, size.y, size.z);
                        
                        // Use a more reasonable scaling approach
                        // Target size: 1.5 units (instead of 2) to leave some margin
                        let totalScale = 1;
                        
                        // If model is very small (< 0.1), assume it's in mm and convert to meters first
                        if (maxDim < 0.1) {
                            model.scale.multiplyScalar(1000); // Convert mm to m
                            totalScale = 1000;
                            // Recalculate bounding box after conversion
                            const convertedBox = new THREE.Box3().setFromObject(model);
                            const convertedSize = convertedBox.getSize(new THREE.Vector3());
                            const convertedMaxDim = Math.max(convertedSize.x, convertedSize.y, convertedSize.z);
                            const finalScale = 1.5 / convertedMaxDim;
                            model.scale.multiplyScalar(finalScale);
                            totalScale = totalScale * finalScale;
                        } else if (maxDim > 10) {
                            // Model is very large, scale it down
                            totalScale = 1.5 / maxDim;
                            model.scale.multiplyScalar(totalScale);
                        } else {
                            // Model is reasonable size, scale to fit
                            totalScale = 1.5 / maxDim;
                            model.scale.multiplyScalar(totalScale);
                        }

                        // Recalculate center after all scaling is done
                        const finalBox = new THREE.Box3().setFromObject(model);
                        const finalCenter = finalBox.getCenter(new THREE.Vector3());
                        
                        // Center the model at origin using the final center
                        model.position.x = -finalCenter.x;
                        model.position.y = -finalCenter.y;
                        model.position.z = -finalCenter.z;
                    }

                    scene.add(model);

                    // Calculate new bounding box after scaling and centering
                    const scaledBox = new THREE.Box3().setFromObject(model);
                    scaledBox.expandByObject(model); // Ensure we get all children
                    const scaledCenter = scaledBox.getCenter(new THREE.Vector3());
                    const scaledSize = scaledBox.getSize(new THREE.Vector3());

                    // Calculate camera distance to fit bounding box within viewport
                    const maxSize = Math.max(scaledSize.x, scaledSize.y, scaledSize.z);
                    const container = containerRef.current;
                    const width = container.clientWidth;
                    const height = container.clientHeight;
                    const aspect = width / height;
                    
                    // Calculate distance needed to fit the bounding box in the viewport
                    // Formula: distance = (size / 2) / tan(fov / 2)
                    // We want the model to fit in the smaller dimension (height) to ensure it fits
                    const fovRad = camera.fov * (Math.PI / 180);
                    const requiredDistance = (maxSize / 2) / Math.tan(fovRad / 2);
                    
                    // Add padding (1.2x) so model doesn't touch edges, and ensure it fits in height
                    // Also account for aspect ratio - if wider, we might need more distance
                    const distance = requiredDistance * 1.2 * Math.max(1, aspect);
                    
                    // Set camera to default starting position
                    camera.position.set(1.0939, 1.9527, -6.3798);
                    camera.lookAt(scaledCenter);
                    camera.updateProjectionMatrix();
                    
                    // Set controls target to model center
                    controls.target.copy(scaledCenter);
                    // Calculate distance from camera to model center for controls
                    const distanceToCenter = camera.position.distanceTo(scaledCenter);
                    controls.minDistance = distanceToCenter * 0.3;
                    controls.maxDistance = distanceToCenter * 5;
                    controls.update();
                    
                    // Remove loading indicator
                    const loadingIndicator = document.getElementById('model-loading-indicator');
                    if (loadingIndicator) {
                        loadingIndicator.remove();
                    }
                    
                    // Force a render
                    if (rendererRef.current && sceneRef.current && cameraRef.current) {
                        rendererRef.current.render(sceneRef.current, cameraRef.current);
                    }
                })
                .catch((error) => {
                    console.error('Error loading model:', error);
                    console.error('Model path was:', modelPath);
                    console.error('Full path was:', fullPath);
                    console.error('Base path was:', basePath);
                    console.error('Error details:', error.message, error.stack);
                    
                    // Test if the file is actually accessible
                    fetch(fullPath, { method: 'HEAD' })
                        .then(response => {
                            console.error('File accessibility test - Status:', response.status);
                            console.error('Content-Type:', response.headers.get('Content-Type'));
                            console.error('Content-Length:', response.headers.get('Content-Length'));
                            if (response.status === 404) {
                                console.error('File not found! Check if file exists at: public' + fullPath);
                            }
                        })
                        .catch(fetchError => {
                            console.error('Could not test file accessibility:', fetchError);
                        });
                    
                    // Remove loading indicator
                    const loadingIndicator = document.getElementById('model-loading-indicator');
                    if (loadingIndicator) {
                        loadingIndicator.remove();
                    }
                    
                    // Display error message in the container
                    if (containerRef.current) {
                        const errorDiv = document.createElement('div');
                        errorDiv.style.cssText = 'position: absolute; top: 50%; left: 50%; transform: translate(-50%, -50%); color: red; background: rgba(0,0,0,0.8); padding: 20px; border-radius: 8px; text-align: center; z-index: 1000; max-width: 90%;';
                        const errorMsg = error.message || 'Check console for details';
                        const isJsonError = errorMsg.includes('JSON.parse');
                        errorDiv.innerHTML = `
                            <p>Error loading 3D model</p>
                            <p style="font-size: 12px; margin-top: 10px;">${errorMsg}</p>
                            ${isJsonError ? '<p style="font-size: 11px; margin-top: 10px; color: #ffaa00;">This usually means the file path is incorrect or the file is not being served correctly.</p>' : ''}
                            <p style="font-size: 10px; margin-top: 5px; color: #999;">Path: ${fullPath}</p>
                            <p style="font-size: 10px; margin-top: 5px; color: #999;">Expected at: public${fullPath}</p>
                        `;
                        containerRef.current.appendChild(errorDiv);
                    }
                });
        } else {
                // For non-GLB files (GLTF), use the standard loader
            loader.loadAsync(fullPath)
                .then((gltf) => {
                    const model = gltf.scene;
                    modelRef.current = model;
                    // ... (same processing as GLB files)
                    scene.add(model);
                })
                .catch((error) => {
                    console.error('Error loading model:', error);
                    const loadingIndicator = document.getElementById('model-loading-indicator');
                    if (loadingIndicator) {
                        loadingIndicator.remove();
                    }
                });
        }

        // Animation loop
        const animate = () => {
            animationFrameRef.current = requestAnimationFrame(animate);
            if (controlsRef.current) {
                controlsRef.current.update();
            }
            if (rendererRef.current && sceneRef.current && cameraRef.current) {
                rendererRef.current.render(sceneRef.current, cameraRef.current);
            }
        };
        animate();

        // Handle resize
        const handleResize = () => {
            if (!containerRef.current || !cameraRef.current || !rendererRef.current) return;
            const width = containerRef.current.clientWidth;
            const height = containerRef.current.clientHeight;
            cameraRef.current.aspect = width / height;
            cameraRef.current.updateProjectionMatrix();
            rendererRef.current.setSize(width, height);
        };
        window.addEventListener('resize', handleResize);

        // Cleanup function
        return () => {
            window.removeEventListener('resize', handleResize);
            if (animationFrameRef.current) {
                cancelAnimationFrame(animationFrameRef.current);
            }
            if (containerRef.current && rendererRef.current) {
                try {
                    containerRef.current.removeChild(rendererRef.current.domElement);
                } catch (e) {
                    // Already removed
                }
            }
            if (rendererRef.current) {
                rendererRef.current.dispose();
            }
            if (sceneRef.current) {
                sceneRef.current.traverse((object) => {
                    if (object.geometry) object.geometry.dispose();
                    if (object.material) {
                        if (Array.isArray(object.material)) {
                            object.material.forEach(material => material.dispose());
                        } else {
                            object.material.dispose();
                        }
                    }
                    if (object.texture) object.texture.dispose();
                });
            }
        };
    }, [modelPath]);

    return (
        <div ref={containerRef} className="model-viewer-container">
            <div style={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                color: '#fff',
                fontSize: '14px',
                zIndex: 100,
                pointerEvents: 'none'
            }} id="model-loading-indicator">
                Loading 3D model...
            </div>
        </div>
    );
};

export default ModelViewer;
